import type { GetStaticPropsContext } from 'next';

import { Box, Flex, Heading, List, ListItem, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { BlueWaveHero } from '~/components/blue-wave-hero';
import { ButtonLink } from '~/components/button-link';
import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { Markdown } from '~/components/markdown';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

const pageContentData = {
  pageTitle: 'Contact Us',
  contactMethods: [
    {
      glyphName: 'chat-bubbles-chat-bubbles-one',
      title: 'FAQs',
      description: 'Quick answers to common questions',
      linkText: 'Help Center',
      linkHref: 'https://support.udacity.com/hc/en-us',
    },
    {
      glyphName: 'book-three',
      title: 'Content Questions',
      description: 'Personalized answers to course or project questions',
      linkText: 'Knowledge',
      linkHref:
        'https://knowledge.udacity.com/?_gl=1*nrj47m*_ga*MTYxODI5Mjg2MC4xNjkxNjkyODgx*_ga_CF22GKVCFK*MTY5MTY5OTU2OC4yLjEuMTY5MTcwMDczNi42MC4wLjA.',
    },
    {
      glyphName: 'avatar-group-three-alt-one',
      title: 'Everything Else',
      description: 'Help from a Udacity team member',
      linkText: 'Submit a Request',
      linkHref: 'https://support.udacity.com/hc/en-us/requests/new',
    },
  ],
  mediaInquiry: {
    glyphName: 'telecom-one',
    description: 'For specific media inquiries, get in touch with our press team.',
    linkText: 'Get In Touch',
    linkHref: 'mailto:media@udacity.com?subject=RE%3A%20Press%20Inquiry',
  },
} as const;

export default function Contact({ page }: { page: Page }) {
  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <BlueWaveHero>
          <Heading as='h1' size='h1' textAlign='center'>
            {pageContentData.pageTitle}
          </Heading>
        </BlueWaveHero>
        <ContentContainer
          padding={{
            base: '40px 16px 80px',
            md: '80px 16px 120px',
            xl: '80px 0px 120px',
          }}
        >
          <List
            display='flex'
            flexDirection={{
              base: 'column',
              xl: 'row',
            }}
            gap={{
              base: '24px',
              md: '32px',
            }}
          >
            {pageContentData.contactMethods.map((method) => (
              <ListItem key={method.title} display='flex' flex='1'>
                <Card
                  as='article'
                  border='none'
                  boxShadow='0px 2px 4px -1px rgba(11, 11, 11, 0.06), 0px 4px 6px -1px rgba(11, 11, 11, 0.10);'
                  display='flex'
                  flexDirection='column'
                  width='100%'
                  minHeight={{
                    base: 'unset',
                    md: '368px',
                  }}
                  padding={{
                    base: '24px',
                    md: '32px 32px 40px',
                  }}
                >
                  <Glyph height={80} name={method.glyphName} width={80} />
                  <Box height='24px' />
                  <Heading as='h2' size='h2'>
                    {method.title}
                  </Heading>
                  <Box height='8px' />
                  <Markdown markdownText={method.description} />
                  <Box height='24px' />
                  <ButtonLink
                    buttonProps={{
                      variant: 'outline',
                      size: 'icon-right',
                      rightIcon: <ArrowRightIcon height='20px' width='20px' />,
                      marginTop: 'auto',
                      width: {
                        base: '100%',
                        md: 'fit-content',
                      },
                    }}
                    linkProps={{
                      href: method.linkHref,
                    }}
                  >
                    {method.linkText}
                  </ButtonLink>
                </Card>
              </ListItem>
            ))}
          </List>
          <Box height='24px' />
          {pageContentData.mediaInquiry && (
            <Card
              as='div'
              borderColor='gray.300'
              padding={{
                base: '24px',
                md: '16px 48px',
              }}
            >
              <Flex
                as='aside'
                alignItems={{
                  md: 'center',
                }}
                flexDirection={{
                  base: 'column',
                  md: 'row',
                }}
              >
                <Glyph height={48} name={pageContentData.mediaInquiry.glyphName} width={48} />
                <Box flex='0 0 8px' />
                <Text
                  color='gray.900'
                  fontWeight='600'
                  marginBottom={{
                    base: '16px',
                    md: '0px',
                  }}
                >
                  {pageContentData.mediaInquiry.description}
                </Text>
                <ButtonLink
                  buttonProps={{
                    variant: 'link',
                    size: 'icon-right',
                    rightIcon: <ArrowRightIcon height='20px' width='20px' />,
                    marginLeft: {
                      base: 'unset',
                      md: 'auto',
                    },
                    paddingLeft: '0px',
                    alignSelf: {
                      base: 'flex-start',
                      md: 'unset',
                    },
                  }}
                  linkProps={{
                    href: pageContentData.mediaInquiry.linkHref,
                  }}
                >
                  {pageContentData.mediaInquiry.linkText}
                </ButtonLink>
              </Flex>
            </Card>
          )}
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/contact');

  return {
    props: {
      page,
    },
  };
}
